import { useCallback, useState } from 'react';
import { Box, Button, FormLabel, IconButton } from '@mui/material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { mask } from 'remask';
import { CloseRounded, UploadRounded } from '@mui/icons-material';
import { toast } from 'react-toastify';

import { HeaderEdit, Page, PreviewArea, SectionInputs } from './styles';

import {
  Account,
  createAvatarAndLogoRequest,
  createCompanyRequest,
  getUserByIdRequest,
  updateCompanyRequest,
  // updateUserRequest,
} from '../../../services';

import useAuthStore from '../../../store/auth';

import { Input } from '../../../components/Input';
import { SelectComponent } from '../../../components/Select';

import {
  cnpjPattern,
  removeDotsAndTracesCPF,
} from '../../../utils/cpfValidation';
import { getNichos } from '../../../utils/companyHelpers';
import { acceptImageTypes } from '../../../utils/mimeTypes';

interface CadastroEmpresaProps {
  getCompanies: any;
  handleReset: any;
  accountId: string;
  conta: Account;
  setIsLoading: (status: boolean) => void;
  setIsLoadingText: (text: string) => void;
}

export const CadastroEmpresa = ({
  getCompanies,
  handleReset,
  accountId,
  conta,
  setIsLoading,
  setIsLoadingText,
}: CadastroEmpresaProps) => {
  const user = useAuthStore(state => state.user);

  const [file, setFile] = useState<any>(null);
  const [url, setUrl] = useState<any>('');
  const [logoName, setLogoName] = useState('');
  const [logoMimetype, setLogoMimetype] = useState('');
  const [logoSize, setLogoSize] = useState(0);
  const [logoFileInBase64, setLogoFileInBase64] = useState<any>();

  const formValidation = useCallback((): any => {
    return Yup.object().shape({
      nome_empresa: Yup.string()
        .min(4, 'Mínimo de 4 Dígitos')
        .required('É necessário adicionar um Nome.'),
      nome_fantasia: Yup.string().min(3, 'Mínimo de 3 Dígitos'),
      cnpj_empresa: Yup.string()
        .min(18, 'Mínimo de 4 Dígitos')
        .required('É necessário adicionar um CNPJ.'),
      nicho_empresa: Yup.string().required('É necessário adicionar um Nicho.'),
    });
  }, []);

  const { control, handleSubmit, setValue, reset } = useForm({
    resolver: yupResolver(formValidation()),
    reValidateMode: 'onBlur',
  });

  const handleCreateCompany = useCallback(
    async (formData: any) => {
      setIsLoadingText('Carregando Informações da Empresa');
      setIsLoading(true);
      try {
        let data = {};

        const accountAdmins = conta?.admins?.map(userAdmin => ({
          id: userAdmin?.id as string,
          name: userAdmin?.name as string,
          email: userAdmin?.email,
          doc: userAdmin?.doc as string,
          avatar_url: userAdmin?.avatar_url,
          profile: 'UN2',
          status: userAdmin?.status,
        }));

        const docsAdmins = conta?.admins?.map(userDoc =>
          removeDotsAndTracesCPF(userDoc.doc as string),
        );

        data = {
          name: formData.nome_empresa,
          fancy_name: formData.nome_fantasia,
          doc: formData.cnpj_empresa,
          niche: formData.nicho_empresa,
          logo_url: '',
          status: true,
          account_id: accountId,
          users: [...(accountAdmins as any)],
          users_ids: [...(docsAdmins as any)],
          boards: [],
          boards_ids: [],
        };

        const arquiveData = {
          original_name: logoName,
          mimetype: logoMimetype,
          size: logoSize,
          localFile: logoFileInBase64,
        };

        const response = await createCompanyRequest(data);
        if (response?.status === 201) {
          const empresa = response?.data;

          let empresaLogoUrl = '';

          if (url !== '') {
            const imageResponse = await createAvatarAndLogoRequest(
              arquiveData as any,
              user?.id as string,
              empresa?.id as string,
            );

            if (imageResponse?.status === 201) {
              const empresaData = {
                logo_url: imageResponse.data.url,
              };

              empresaLogoUrl = imageResponse.data.url;

              await updateCompanyRequest(empresaData, empresa.id);
            }
          }

          const responseUsers = await Promise.all(
            conta?.admins?.map(async (uAA: any) => {
              const userInfo = await getUserByIdRequest(uAA.id);

              return userInfo;
            }) as any,
          );

          let responseSuccess: boolean[] = [];

          // eslint-disable-next-line no-restricted-syntax
          // for (const userAdm of responseUsers as any) {
          //   const userUpdate = {
          //     companies: [
          //       ...userAdm.data.companies,
          //       {
          //         id: empresa?.id,
          //         name: empresa?.name,
          //         doc: empresa?.doc,
          //         niche: empresa?.niche,
          //         status: empresa.status,
          //         logo_url: empresaLogoUrl,
          //         account_id: empresa.account_id,
          //       },
          //     ] as any,
          //     companies_ids: [
          //       ...(userAdm.data?.companies_ids as string[]),
          //       empresa?.id,
          //     ],
          //   };
          //   console.log(userUpdate, 'User Loop');
          //   // eslint-disable-next-line no-await-in-loop
          //   const responseUser = await updateUserRequest(
          //     userAdm.data?.id as string,
          //     userUpdate,
          //   );

          //   if (responseUser?.status === 200) {
          //     responseSuccess = [...responseSuccess, true];
          //   } else {
          //     responseSuccess = [...responseSuccess, false];
          //   }
          // }

          const usersUpdatedSuccess = responseSuccess.some(uUS => uUS === true);

          getCompanies();
          toast.success('Empresa criada com sucesso');
          if (usersUpdatedSuccess) {
            toast.success('Usuários editados com sucesso');
          }

          handleReset();
        }
      } catch (error) {
        console.log('Error handleCreateCompany', error);
      } finally {
        setIsLoading(false);
      }
    },
    [
      setIsLoadingText,
      setIsLoading,
      conta?.admins,
      accountId,
      logoName,
      logoMimetype,
      logoSize,
      logoFileInBase64,
      url,
      getCompanies,
      handleReset,
      user?.id,
    ],
  );

  const handleLogo = useCallback((t: any) => {
    const { files } = t.target;

    let reader = new FileReader();
    reader.readAsDataURL(files[0]);

    reader.onload = () => {
      setLogoFileInBase64(reader.result);
    };
    reader.onerror = error => {
      console.log('convertFileInBase64 error => ', error);
      return null;
    };

    const fileName = files[0].name;
    const fileSize = files[0].size;
    const fileType = files[0].type;

    const newUrl = URL.createObjectURL(files[0]);

    setLogoName(fileName);
    setLogoMimetype(fileType);
    setLogoSize(fileSize);

    setFile(files[0]);
    setUrl(newUrl);
  }, []);

  const handleCancel = useCallback(() => {
    handleReset();
    reset();
  }, [reset, handleReset]);

  return (
    <Box width="480px" margin="20px">
      <HeaderEdit>
        <IconButton onClick={handleCancel}>
          <CloseRounded />
        </IconButton>
        <Box className="add-company">Adicionar Empresa</Box>
      </HeaderEdit>
      <Page>
        <form onSubmit={handleSubmit(handleCreateCompany)}>
          <SectionInputs>
            {url ? (
              <PreviewArea
                style={{
                  width: '100%',
                  height: '260px',
                  marginBottom: 20,
                }}
              >
                <img src={url} alt="logo" />
              </PreviewArea>
            ) : (
              <FormLabel htmlFor="input-file" className="add-logo">
                <input
                  type="file"
                  name="file"
                  value={file}
                  onChange={handleLogo}
                  id="input-file"
                  style={{ display: 'none' }}
                  accept={acceptImageTypes()}
                />
                <UploadRounded /> Inserir Logotipo
              </FormLabel>
            )}
            <Input
              name="nome_empresa"
              control={control}
              fullWidth
              label="Razão social"
            />
            <Input
              name="nome_fantasia"
              control={control}
              fullWidth
              label="Nome fantasia ou apelido"
            />
            <Input
              name="cnpj_empresa"
              control={control}
              fullWidth
              label="CNPJ"
              onChange={t =>
                setValue('cnpj_empresa', mask(t.target.value, cnpjPattern))
              }
            />
            <SelectComponent
              fullWidth
              label="Nicho"
              name="nicho_empresa"
              control={control}
              placeholder="Nicho"
              options={getNichos}
            />
          </SectionInputs>
          <Button
            type="submit"
            variant="contained"
            color="success"
            sx={{ color: '#fff' }}
            className="btn-save"
          >
            Salvar
          </Button>
        </form>
      </Page>
    </Box>
  );
};
